import React from 'react';

function PriceSummary({ items, artikel, set, rabattAktiv }) {
  if (!artikel) return null;

  // Anzahl pro Artikel summieren
  const counts = {};
  for (const [key, art] of Object.entries(artikel)) {
    if (!art.groessen || art.groessen.length === 0) {
      counts[key] = items[`${key}_anzahl`] || 0;
    } else {
      counts[key] = art.groessen.reduce((sum, g) => sum + (items[`${key}_${g}`] || 0), 0);
    }
  }

  const hasItems = Object.values(counts).some((c) => c > 0);

  if (!hasItems) {
    return (
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">Zusammenfassung</h5>
        </div>
        <div className="card-body text-center text-muted">
          <p className="mb-0">Bitte wählen Sie mindestens einen Artikel aus.</p>
        </div>
      </div>
    );
  }

  // Set-Rabatt berechnen
  const setArtikel = Object.entries(artikel).filter(([, a]) => a.set_rabatt);
  let autoSetCount = 0;
  let setTotal = 0;
  let setErsparnis = 0;

  if (rabattAktiv && setArtikel.length > 0) {
    const setCounts = setArtikel.map(([key]) => counts[key] || 0);
    autoSetCount = Math.min(...setCounts);
    setTotal = autoSetCount * (set?.preis || 0);
    const einzelPreisSumme = setArtikel.reduce((sum, [, a]) => sum + a.preis, 0);
    setErsparnis = autoSetCount * (einzelPreisSumme - (set?.preis || 0));
  }

  // Gesamtsumme
  let gesamtTotal = 0;
  if (rabattAktiv && autoSetCount > 0) {
    gesamtTotal += setTotal;
    for (const [key, art] of Object.entries(artikel)) {
      const einzeln = art.set_rabatt ? counts[key] - autoSetCount : counts[key];
      gesamtTotal += einzeln * art.preis;
    }
  } else {
    for (const [key, art] of Object.entries(artikel)) {
      gesamtTotal += counts[key] * art.preis;
    }
  }

  // Tabellenzeilen aufbauen
  const rows = [];

  if (rabattAktiv && autoSetCount > 0) {
    rows.push(
      <tr key="set" className="table-success">
        <td>
          <strong>Set-Rabatt</strong>
          <small className="text-muted d-block">
            {setArtikel.map(([, a]) => `${autoSetCount}× ${a.name}`).join(' + ')}
          </small>
        </td>
        <td className="text-end">{autoSetCount} × {set?.preis} €</td>
        <td className="text-end fw-bold" style={{ width: '100px' }}>
          {setTotal.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
        </td>
      </tr>
    );
  }

  for (const [key, art] of Object.entries(artikel)) {
    const anzahl = rabattAktiv && art.set_rabatt ? counts[key] - autoSetCount : counts[key];
    if (anzahl <= 0) continue;

    const subtotal = anzahl * art.preis;
    const label =
      rabattAktiv && art.set_rabatt && autoSetCount > 0
        ? `${art.name} (einzeln)`
        : art.name;

    rows.push(
      <tr key={key}>
        <td>{label}</td>
        <td className="text-end">{anzahl} × {art.preis} €</td>
        <td className="text-end fw-bold" style={{ width: '100px' }}>
          {subtotal.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
        </td>
      </tr>
    );
  }

  return (
    <div className="card mb-4">
      <div className="card-header">
        <h5 className="mb-0">Zusammenfassung</h5>
      </div>
      <div className="card-body">
        <table className="table table-sm mb-0">
          <tbody>{rows}</tbody>
          <tfoot>
            <tr className="table-primary">
              <td colSpan="2" className="fw-bold">Gesamtsumme</td>
              <td className="text-end fw-bold fs-5">
                {gesamtTotal.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
              </td>
            </tr>
          </tfoot>
        </table>

        {rabattAktiv && setErsparnis > 0 && (
          <div className="alert alert-success mt-3 mb-0 py-2" role="status" aria-live="polite">
            <small>
              <strong>
                Sie sparen {setErsparnis.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
              </strong>{' '}
              durch den automatischen Set-Rabatt!
            </small>
          </div>
        )}
      </div>
    </div>
  );
}

export default PriceSummary;
